import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { types as sdkTypes } from '../../util/sdkLoader';
import { FormattedMessage, injectIntl } from 'react-intl';
const { LatLngBounds, LatLng } = sdkTypes;

import IconClose from '../IconClose/IconClose';
import css from './AlgoliaLocationSearch.module.css';
import classNames from 'classnames';
import Cookies from 'js-cookie';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentUserCurrency, setCurrentUserLocationInfo } from '../../ducks/user.duck';
import { getUserDefaultCurrency } from '../../util/data';
import { supportedCountries } from '../../config/configStripe';
import { isAuthenticatedSelector } from '../../ducks/auth.duck';
import { DEFAULT_CURRENCY, LOCATIONINFO_COOKIE_KEY } from '../../util/constants';
import IconCollection from '../IconCollection/IconCollection';
import { getCurrentLocationCoords, clearLocationWatch } from '../../util/data';

const mapboxToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
const PREDICTIONS_KEY = 'predictions';
const MAX_PREDICTIONS = 3;
const DEFAULT_LOCATION = 'Ottawa';
const DEFAULT_LOCATION_ERROR = "Please select your search area."
const PREDICTION_ERROR = "Please select a valid address."

function shrinkBBox(bbox, shrinkFactor = 0.4) {
  if (!bbox || bbox.length !== 4) return bbox;

  const [minLon, minLat, maxLon, maxLat] = bbox;

  const lonDiff = (maxLon - minLon) * shrinkFactor;
  const latDiff = (maxLat - minLat) * shrinkFactor;

  return [
    minLon + lonDiff, // move east
    minLat + latDiff, // move north
    maxLon - lonDiff, // move west
    maxLat - latDiff  // move south
  ];
}

const getCityFromMapBoxLocation = data => {
  const { features = [] } = data || {};
  if (!features.length) {
    return null;
  }

  const addressObj = features.find(d => d.id.startsWith('address'));
  const pointObj = features.find(d => d.id.startsWith('poi'));
  const localityObj = features.find(d => d.id.startsWith('locality'));
  const address = !!pointObj
    ? pointObj
    : !!addressObj
      ? addressObj
      : !!localityObj
        ? localityObj
        : null;

  if (!address) {
    return null;
  }

  const { place_name, context } = address;
  const locationInfo = context.reduce((info, item) => {
    const { id, text } = item;
    const name = id.split('.')[0];

    if (['country', 'place', 'district', 'postcode'].includes(name)) {
      info[name] = text;
    }

    return info;
  }, {});

  const { place } = locationInfo;

  return place;
};

/**
 * store prediction to local host
 * @param {*} data
 */
const storePredictions = prediction => {
  if (!prediction || !prediction.id || typeof window === 'undefined') {
    return;
  }

  const storedPredictions = window.localStorage.getItem(PREDICTIONS_KEY);
  const parsedPredictions = storedPredictions
    ? JSON.parse(storedPredictions)
    : [];

  // Use a Map for uniqueness and easy manipulation
  const predictionsMap = new Map(
    parsedPredictions.map(pred => [pred.id, pred])
  );

  // Add new prediction at the beginning
  predictionsMap.delete(prediction.id); // Ensure no duplicates
  const newPredictions = [prediction, ...Array.from(predictionsMap.values())];

  // Keep only the first MAX_PREDICTIONS entries
  const limitedPredictions = newPredictions.slice(0, MAX_PREDICTIONS);

  // Store updated predictions in localStorage
  window.localStorage.setItem(
    PREDICTIONS_KEY,
    JSON.stringify(limitedPredictions)
  );
};

/**
 * Get predictions from the local storage
 * @returns predictions array
 */
const getPredictions = () => {
  if (typeof window === 'undefined') {
    return [];
  }

  const storedPredictions = window.localStorage.getItem(PREDICTIONS_KEY);
  return storedPredictions ? JSON.parse(storedPredictions) : [];
};

const fetchAndApplyPrediction = async (locationName, handlePredictionClick) => {
  const mapBoxResponse = await axios.get(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${locationName}.json?access_token=${mapboxToken}`
  );

  const prediction = mapBoxResponse?.data?.features?.[0];
  if (!prediction) {
    throw 'Invalid prediction';
  }

  return handlePredictionClick(prediction);
};

const useIPBasedLocation = async ({
  setInputValue,
  setInputFocused,
  setError,
  handlePredictionClick,
}) => {
  try {
    const response = await axios.get('https://ipapi.co/json/');
    const { city, country_name: country } = response.data;

    if (!city) {
      throw 'Invalid city';
    }

    setInputValue(city);
    setInputFocused(false);

    await fetchAndApplyPrediction(city, handlePredictionClick);
  } catch (error) {
    setError(DEFAULT_LOCATION_ERROR);
  }
};

/**
 * Extract the most specific location component from Mapbox place_name
 * Handles various address formats across countries
 * @param {string} placeName - The place_name from Mapbox (e.g., "Kanata, Ontario, Canada")
 * @param {string} region - The region/province to exclude if found as first part
 * @returns {string|null} - The most specific location component
 */
const extractMostSpecificFromPlaceName = (placeName, region) => {
  if (!placeName) return null;

  const parts = placeName.split(',').map(p => p.trim()).filter(Boolean);
  if (parts.length < 2) return null; // Need at least 2 parts for meaningful extraction

  // Iterate through parts to find the most specific (first non-generic, non-numeric component)
  for (let i = 0; i < Math.min(parts.length - 1, 3); i++) { // Check first 3 parts max
    const part = parts[i];

    // Skip if:
    // - Contains numbers (likely street address: "123 Main St" or "5th Avenue")
    // - Is the region we already know (avoid duplication)
    // - Too long (likely full address text)
    // - Is a known generic term
    const isStreetAddress = /^\d+/.test(part) || /\d+\s*(st|street|ave|avenue|rd|road|blvd|boulevard|dr|drive|ln|lane|way|ct|court|pl|place)/i.test(part);
    const isTooLong = part.length > 50;
    const isRegion = region && part.toLowerCase() === region.toLowerCase();
    const isGeneric = ['the', 'and', 'of', 'in', 'on', 'at'].includes(part.toLowerCase());

    if (!isStreetAddress && !isTooLong && !isRegion && !isGeneric && part.length > 1) {
      return part;
    }
  }

  return null;
};

const LocationSearch = props => {
  const {
    defaultLocation,
    initialValues,
    name,
    onRefineLocation,
    className,
    isLandingPage = false,
    isEditListingPage = false,
    isMapExploring = false,
    showInlineError = true,
    intl,
  } = props;
  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState('');
  const [predictions, setPredictions] = useState([]);
  const [error, setError] = useState('');
  const [inputFocused, setInputFocused] = useState(false);
  const [locationAccessAllowed, setLocationAccessAllowed] = useState(true);
  const blurTimeout = useRef(null);
  const dropdownRef = useRef(null);
  const isAuthenticated = useSelector(isAuthenticatedSelector);

  const storedPredictions = getPredictions();

  // Check if location access is allowed
  useEffect(() => {
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'geolocation' })
        .then(permissionStatus => {
          setLocationAccessAllowed(permissionStatus.state === 'granted');

          // Listen for changes in permission status
          permissionStatus.onchange = () => {
            setLocationAccessAllowed(permissionStatus.state === 'granted');
          };
        })
        .catch(() => {
          // If permissions API is not supported, we'll check when user tries to use location
          setLocationAccessAllowed(false);
        });
    } else {
      // For browsers that don't support permissions API
      setLocationAccessAllowed(false);
    }
  }, []);

  // Show predictions only if input is in focused.
  const showPredictions = inputFocused;

  const { bounds, address } = initialValues || {};
  const defaultAddress = !!address ? address : defaultLocation?.address;
  useEffect(() => {
    if (isMapExploring) {
      // When exploring map, show "Results in map area" but don't set it as actual value
      // This allows user to still see their original selection
      setInputValue('');
      setError(null);
    } else if (defaultAddress) {
      setInputValue(defaultAddress);
      setInputFocused(false);
      setError(null);
    } else {
      setError(DEFAULT_LOCATION_ERROR);
    };
  }, [defaultAddress, isMapExploring]);

  useEffect(() => {
    if (inputValue) {
      fetchPredictions(inputValue);
    } else {
      setPredictions([]);
    }
  }, [inputValue]);

  const fetchPredictions = async query => {
    try {
      const response = await axios.get(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json?access_token=${mapboxToken}`
      );
      // console.log('response', response.data.features);
      setPredictions(response.data.features);
    } catch (error) {
      console.error('Error fetching predictions:', error);
    }
  };

  const handleInputChange = e => {
    setInputFocused(true);
    setInputValue(e.target.value);
    setError('');
  };

  const handleInputFocus = () => {
    setInputFocused(prev => {
      if (prev) return null;
      return true;
    });
    setError('');
  };

  const handleInputBlur = () => {
    // Delay input blur for some time so
    // user can select value from the recommendations.
    blurTimeout.current = setTimeout(() => {
      if (predictions && predictions.length && !predictions.map(p => p.place_name).includes(inputValue) && inputValue !== defaultAddress) {
        setError(PREDICTION_ERROR);
        setInputValue('');
      } else if (!inputValue) {
        setError(DEFAULT_LOCATION_ERROR);
      }
      setInputFocused(false);
    }, 500);
  };

  const handlePredictionClick = (prediction, isPredictionClicked = false) => {
    if (blurTimeout && blurTimeout.current) {
      clearTimeout(blurTimeout.current);
    }

    const { bbox = [], center = [] } = prediction || {};

    // swLng (southwest longitude), swLat (southwest latitude)
    // neLng (northeast longitude), neLat (northeast latitude)
    const [swLng, swLat, neLng, neLat] = bbox;

    const centerLng = center[0]; // Mapbox center[0] = lng
    const centerLat = center[1]; // Mapbox center[1] = lat

    const kmToDegLat = 20 / 111;
    const kmToDegLng = 20 / (111 * Math.cos(centerLat * Math.PI / 180)); // ✅ correct: use latitude here

    const expandedBounds =
      bbox && bbox.length === 4
        ? bbox
        : null;

    const constructedBounds =
      center && center.length === 2
        ? [
          centerLng - kmToDegLng, // ✅ swLng
          centerLat - kmToDegLat, // ✅ swLat
          centerLng + kmToDegLng, // ✅ neLng
          centerLat + kmToDegLat  // ✅ neLat
        ]
        : null;

    let selectedBounds =
      expandedBounds
        ? {
          southWest: { lng: expandedBounds[0], lat: expandedBounds[1] },
          northEast: { lng: expandedBounds[2], lat: expandedBounds[3] }
        }
        : constructedBounds
          ? {
            southWest: { lng: constructedBounds[0], lat: constructedBounds[1] },
            northEast: { lng: constructedBounds[2], lat: constructedBounds[3] }
          }
          : null;

    if (prediction && selectedBounds) {
      // Extract location components following Mapbox hierarchy (most specific first)
      // Hierarchy: locality > neighborhood > place > district > region > country
      const locality = prediction.context?.find(c => c.id.startsWith('locality'))?.text;
      const neighborhood = prediction.context?.find(c => c.id.startsWith('neighborhood'))?.text;
      const place = prediction.context?.find(c => c.id.startsWith('place'))?.text;
      const district = prediction.context?.find(c => c.id.startsWith('district'))?.text;
      const region = prediction.context?.find(c => c.id.startsWith('region'))?.text;
      const country = prediction.context?.find(c => c.id.startsWith('country'))?.text;

      // Prefer most specific location following Mapbox's hierarchy
      let selectedArea = locality || neighborhood || place || district;

      // Fallback: Smart parsing from place_name if context doesn't have specific location
      // This handles cases where Mapbox returns location but context is incomplete
      if (!selectedArea && prediction.place_name) {
        selectedArea = extractMostSpecificFromPlaceName(prediction.place_name, region);
      }

      // Final fallback to region only if absolutely nothing else is available
      // This should rarely happen, but ensures we always have something to display
      if (!selectedArea) {
        selectedArea = region;
      }

      setInputValue(prediction.place_name);
      setError('');
      setInputFocused(false);

      const { southWest, northEast } = selectedBounds;
      const swLatLng = new LatLng(southWest.lat, southWest.lng);
      const neLatLng = new LatLng(northEast.lat, northEast.lng);
      const latlngBounds = new LatLngBounds(neLatLng, swLatLng);

      const latLng = new LatLng(centerLat, centerLng); // ✅ flip order here too

      // console.log('prediction', prediction);
      const payload = {
        address: prediction.place_name,
        bounds: latlngBounds,
        origin: latLng,
        city: selectedArea,
        country: country
      };

      onRefineLocation({
        ...payload,
        isLandingPage
      });

      if (isPredictionClicked) {
        const predictionCountry = prediction.id.startsWith('country')
          ? prediction
          : prediction.context?.find(c => c.id.startsWith('country'))
        const predictionCountryCode = predictionCountry
          ? (predictionCountry.properties?.short_code?.toUpperCase() || predictionCountry.short_code?.toUpperCase())
          : null;

        const cookieLocationData = {
          ...payload,
          country: predictionCountryCode
        };

        Cookies.set(LOCATIONINFO_COOKIE_KEY, JSON.stringify(cookieLocationData), {
          expires: 30,
          secure: true
        });

        const currency = getUserDefaultCurrency(
          supportedCountries.find(({ country }) =>
            prediction.place_name.includes(country)
          )?.code
        );

        dispatch(setCurrentUserCurrency(currency || DEFAULT_CURRENCY));
        dispatch(setCurrentUserLocationInfo({ ...cookieLocationData }));
      }

      storePredictions(prediction);
    }
  };

  const handleCurrentLocationClick = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    // Function to handle successful position retrieval
    const handlePositionSuccess = async (position) => {
      try {
        const { latitude, longitude } = position;
        const response = await axios.get(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${mapboxToken}`
        );

        const prediction = response?.data?.features?.[0];
        if (!prediction) {
          throw new Error('Could not get location details');
        }

        handlePredictionClick(prediction, true);
        setError('');
        setLocationAccessAllowed(true);
      } catch (error) {
        console.error('Error getting location details:', error);
        setError(DEFAULT_LOCATION_ERROR);
      }
    };

    // Get current position using our optimized function
    getCurrentLocationCoords()
      .then(position => {
        if (position && position?.latitude && position?.longitude) {
          handlePositionSuccess(position);
        } else {
          setError(DEFAULT_LOCATION_ERROR);
        }
      })
      .catch(error => {
        console.error('Geolocation error:', error);
        setError(error);
        setInputValue('');
      });
  };

  // Cleanup location watch when component unmounts
  useEffect(() => {
    return () => {
      clearLocationWatch();
    };
  }, []);

  const handleMouseEnter = index => {
    const items = dropdownRef.current.querySelectorAll('li');
    items.forEach((item, idx) => {
      item.classList.toggle(css.highlighted, idx === index);
    });
  };

  // Determine what to show in the dropdown
  const renderDropdownContent = () => {
    // If user is typing and we have predictions, show those

    if (inputFocused) {
      return (
        <>
          <div className={css.topContent}>
            <li
              className={css.dropdownItem}
              onClick={handleCurrentLocationClick}
            >
              <IconCollection name="prediction_pin" />
              <FormattedMessage
                id="AlgoliaLocationSearch.useCurrentLocation"
                defaultMessage="Use current location"
              />
            </li>
          </div>
          {predictions.length > 0
            ? (predictions.map((prediction, index) => (
              <li
                key={prediction.id}
                onClick={() => handlePredictionClick(prediction, true)}
                onMouseEnter={() => handleMouseEnter(index)}
                className={css.dropdownItem}
              >
                <IconCollection name="predictionLocation" />
                {prediction.place_name}
              </li>
            )))
            : storedPredictions.length > 0
              ? (
                <>
                  <div className={css.topContent}>
                    <div className={css.topContentTitle}>
                      <FormattedMessage
                        id="AlgoliaLocationSearch.previouslyViewedLocations"
                        defaultMessage="Previously Viewed Locations"
                      />
                    </div>
                  </div>
                  {storedPredictions.map((prediction, index) => (
                    <li
                      key={prediction.id}
                      onClick={() => handlePredictionClick(prediction, true)}
                      onMouseEnter={() => handleMouseEnter(index)}
                      className={css.dropdownItem}
                    >
                      <IconCollection name="predictionLocation" />
                      {prediction.place_name}
                    </li>
                  ))}
                </>
              )
              : null
          }
        </>
      );
    }

    return null;
  };

  return (
    <div className={classNames(className, css.container, isEditListingPage && css.editListingContainer)}>
      <div className={css.inputWrapper}>
        <input
          type="text"
          name={name}
          value={isMapExploring && !inputFocused && !inputValue
            ? intl.formatMessage({
              id: "AlgoliaLocationSearch.resultsInMapArea",
              defaultMessage: "Results in map area"
            })
            : inputValue}
          onFocus={handleInputFocus}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          className={showInlineError && error ? css.inputError : css.input}
          placeholder={showInlineError && error
            ? error
            : intl.formatMessage({
              id: "AlgoliaLocationSearch.enterLocation",
              defaultMessage: "Enter a location"
            })}
          autoComplete="off"
          readOnly={isMapExploring && !inputFocused && !inputValue}
        />
        {!!inputValue && !(isMapExploring && !inputFocused) ? (
          <button
            className={css.clearLocation}
            type="button"
            onClick={() => {
              setInputValue('');
              setError('');
            }}
          >
            <IconClose size="small" />
          </button>
        ) : null}
      </div>
      {showPredictions && (
        <ul className={css.dropdown} ref={dropdownRef}>
          {renderDropdownContent()}
        </ul>
      )}
    </div>
  );
};

export default injectIntl(LocationSearch);